import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { FaCheckCircle, FaTimesCircle, FaSpinner } from 'react-icons/fa';
import apiClient from '../apiClient';

export default function VerifyEmail() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('verifying'); // verifying, success, error, expired
  const [message, setMessage] = useState('');
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const verifyEmail = async () => {
      const token = searchParams.get('token');
      
      if (!token) {
        setStatus('error');
        setMessage('No verification token provided.');
        setIsLoading(false);
        return;
      }

      try {
        const response = await apiClient.post('/auth/verify-email', { token });
        
        if (response.status === 200) {
          setStatus('success');
          setMessage(response.data.message || 'Email verified successfully!');
          
          // Redirect to login after 3 seconds
          setTimeout(() => {
            navigate('/signin');
          }, 3000);
        }
      } catch (error) {
        console.error('Verification error:', error);
        
        if (error.response?.status === 400) {
          const errorMessage = error.response.data.error;
          if (errorMessage.includes('expired')) {
            setStatus('expired');
            setMessage('Your verification link has expired. Please register again.');
          } else {
            setStatus('error');
            setMessage(errorMessage || 'Invalid verification token.');
          }
        } else {
          setStatus('error');
          setMessage('An error occurred while verifying your email. Please try again.');
        }
      } finally {
        setIsLoading(false);
      }
    };

    verifyEmail();
  }, [searchParams, navigate]);

  const getIcon = () => {
    switch (status) {
      case 'verifying':
        return <FaSpinner className="text-6xl text-blue-600 animate-spin" />;
      case 'success':
        return <FaCheckCircle className="text-6xl text-green-600" />;
      case 'expired':
      case 'error':
        return <FaTimesCircle className="text-6xl text-red-600" />;
      default:
        return null;
    }
  };

  const getStatusColor = () => {
    switch (status) {
      case 'success':
        return 'text-green-600';
      case 'expired':
      case 'error':
        return 'text-red-600';
      default:
        return 'text-blue-600';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
        {/* Logo */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-blue-900">ezOCM</h1>
          <p className="text-gray-600 mt-2">Change Management Platform</p>
        </div>

        {/* Icon */}
        <div className="mb-6">
          {getIcon()}
        </div>

        {/* Status Message */}
        <h2 className={`text-2xl font-semibold mb-4 ${getStatusColor()}`}>
          {status === 'verifying' && 'Verifying Your Email...'}
          {status === 'success' && 'Email Verified!'}
          {status === 'expired' && 'Verification Link Expired'}
          {status === 'error' && 'Verification Failed'}
        </h2>

        {/* Description */}
        <p className="text-gray-700 mb-8">
          {message}
        </p>

        {/* Actions */}
        {status === 'success' && (
          <div>
            <p className="text-sm text-gray-600 mb-4">
              Redirecting to login in 3 seconds...
            </p>
            <button
              onClick={() => navigate('/signin')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium"
            >
              Go to Login Now
            </button>
          </div>
        )}

        {(status === 'expired' || status === 'error') && (
          <div className="space-y-3">
            <button
              onClick={() => navigate('/register')}
              className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors font-medium w-full"
            >
              Register Again
            </button>
            <button
              onClick={() => navigate('/')}
              className="bg-gray-200 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-300 transition-colors font-medium w-full"
            >
              Back to Home
            </button>
          </div>
        )}

        {/* Loading State */}
        {isLoading && status === 'verifying' && (
          <p className="text-sm text-gray-600 animate-pulse">
            Please wait while we verify your email address...
          </p>
        )}
      </div>

      {/* Help Text */}
      <div className="absolute bottom-4 left-0 right-0 text-center">
        <p className="text-sm text-gray-600">
          Having trouble? Contact support at{' '}
          <a href="mailto:support@ezocm.com" className="text-blue-600 hover:underline">
            support@ezocm.com
          </a>
        </p>
      </div>
    </div>
  );
}